import React, { useState } from "react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, Button, Grid, IconButton } from "@mui/material";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import SignatureInput from "../../../../../../components/signiture/SignatureInput";
import { useSignatureController } from "../../../../../../components/signiture/use-signature-controller";

interface AIConsentFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const AIConsentForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched: _touched,
  errors: _errors,
  viewOnly,
}: AIConsentFormProps) => {
  const [_k, setK] = useState<boolean>(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    _k,
    values?.aiConsent?.sign
  );

  return (
    <Box>
      <Text variant="subtitle1" fontWeight="500" mb={1}>
        Consent for Use of Artificial Intelligence (AI)
      </Text>

      {/* Scrollable Document Content Box */}
      <Box
        sx={{
          border: "1px solid #c0c0c0",
          borderRadius: "4px",
          p: 2,
          mb: 3,
          backgroundColor: "#ffffff",
          fontFamily: "sans-serif",
          fontSize: "0.9rem",
          lineHeight: "1.5",
          color: "#333333",
        }}
      >
        <p>
          As part of their ongoing commitment to provide the best possible
          service, your provider has opted to use an artificial intelligence
          note-taking tool that assists in generating clinical documentation
          based on your sessions. This allows for more time and focus to be
          spent on our interactions instead of taking time to jot down notes or
          trying to remember all the important details. A temporary recording
          and transcript or summary of the conversation may be created and used
          to generate the clinical note for that session. Your provider then
          reviews the content of that note to ensure its accuracy and
          completeness. After the note has been created, the recording and
          transcript are automatically deleted.
        </p>

        <p>
          This artificial intelligence tool prioritizes the privacy and
          confidentiality of your personal health information. Your session
          information is strictly used for the purpose of your ongoing medical
          care. Your information is subject to strict data privacy regulations
          and is always secured and encrypted. Stringent business associate
          agreements ensure data privacy and HIPAA compliance. Please discuss
          any questions or concerns you may have about this feature with your
          provider.
        </p>

        <p>
          By signing this form, you consent to the use of artificial
          intelligence as described. You acknowledge that your participation is
          voluntary and not a condition of receiving services from your
          clinician, and that you can withdraw your consent at any time.
        </p>
      </Box>

      {/* Acknowledgment & Signature Field Block */}
      <Box>
        <Text variant="body1" fontWeight="500" mb={0.5}>
          Acknowledgment
        </Text>
        <Text variant="body2" color="textSecondary" mb={2}>
          I have read, understood, and agree to the items contained in this
          document, and agree to sign electronically.
        </Text>

        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <Input
              label="Signed By:"
              textFieldProps={{
                name: "aiConsent.signedByName",
                value: values?.aiConsent?.signedByName || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Your Legal Name",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid size={12}>
            {viewOnly || values?.aiConsent?.sign ? (
              <ErrorHelper label="Signature">
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    width: "368px",
                    height: "100%",
                  }}
                >
                  <img
                    src={values?.aiConsent?.sign}
                    alt="Consent for use of Artificial Intelligence signature"
                    style={{ height: "150px", width: "360px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue("aiConsent.sign", undefined);
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            ) : (
              <Box
                onMouseLeave={() => {
                  if (!isSignEmpty() && signPad?.toDataURL()) {
                    setFieldValue("aiConsent.sign", signPad?.toDataURL());
                  }
                }}
                sx={{
                  width: "368px",
                  position: "relative",
                }}
              >
                <SignatureInput
                  label="Signature"
                  canvasRef={canvasRef}
                  w="368px"
                  h="100px"
                />

                <Button
                  variant="contained"
                  sx={{
                    position: "absolute",
                    bottom: 10,
                    left: "300px",
                    height: "24px",
                  }}
                  onClick={() => {
                    setK((prev) => !prev);
                    signPad?.clear();
                  }}
                >
                  clear
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AIConsentForm;
